import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, User, ArrowRight, CheckCircle2 } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { THANK_YOU_PATH } from "../lib/paths";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function NewsletterForm() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState("");

  const submit = (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      setError(t("newsletter.error_required"));
      return;
    }
    if (!EMAIL_REGEX.test(email.trim())) {
      setError(t("newsletter.error_email"));
      return;
    }
    if (!consent) {
      setError(t("newsletter.error_consent"));
      return;
    }
    setError("");
    // Persist locally (frontend-only lead capture) and navigate to Thank You.
    try {
      const leads = JSON.parse(localStorage.getItem("dvq_leads") || "[]");
      leads.push({ name: name.trim(), email: email.trim(), ts: Date.now() });
      localStorage.setItem("dvq_leads", JSON.stringify(leads));
    } catch (_) {
      /* ignore */
    }
    navigate(THANK_YOU_PATH);
  };

  return (
    <form
      onSubmit={submit}
      noValidate
      data-testid="newsletter-form"
      className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8"
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="mb-1.5 block text-xs font-semibold uppercase tracking-widest text-slate-600">
            {t("newsletter.name_label")}
          </span>
          <div className="relative">
            <User className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
            <input
              type="text"
              name="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("newsletter.name_placeholder")}
              data-testid="newsletter-name-input"
              autoComplete="name"
              className="w-full rounded-md border border-slate-300 bg-white py-3 pl-9 pr-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-slate-900 focus:outline-none focus:ring-1 focus:ring-slate-900"
            />
          </div>
        </label>
        <label className="block">
          <span className="mb-1.5 block text-xs font-semibold uppercase tracking-widest text-slate-600">
            {t("newsletter.email_label")}
          </span>
          <div className="relative">
            <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
            <input
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("newsletter.email_placeholder")}
              data-testid="newsletter-email-input"
              autoComplete="email"
              className="w-full rounded-md border border-slate-300 bg-white py-3 pl-9 pr-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-slate-900 focus:outline-none focus:ring-1 focus:ring-slate-900"
            />
          </div>
        </label>
      </div>

      <label className="mt-4 flex items-start gap-3 text-sm text-slate-700">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          data-testid="newsletter-consent-checkbox"
          className="mt-1 h-4 w-4 rounded border-slate-400 text-slate-900 focus:ring-slate-900"
        />
        <span>{t("newsletter.consent")}</span>
      </label>

      {error && (
        <p
          data-testid="newsletter-error"
          role="alert"
          className="mt-3 text-sm font-medium text-red-700"
        >
          {error}
        </p>
      )}

      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <button
          type="submit"
          data-testid="newsletter-submit-button"
          className="inline-flex items-center justify-center gap-2 rounded-md bg-slate-900 px-5 py-3 text-sm font-semibold text-white hover:bg-slate-800"
        >
          {t("newsletter.submit")}
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </button>
        <p className="flex items-center gap-2 text-xs text-slate-500">
          <CheckCircle2 className="h-4 w-4 text-emerald-600" aria-hidden="true" />
          {t("newsletter.reassurance")}
        </p>
      </div>
    </form>
  );
}
